import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, CheckSquare, StickyNote, X, Trash2, ExternalLink } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { formatRelativeTime, useNow } from '../lib/relativeTime';

interface QuickCapture {
  id: string;
  text: string;
  status: string;
  triaged_to_task_id: string | null;
  triaged_to_note_id: string | null;
  triaged_to_meeting_id: string | null;
  created_at: string;
  updated_at: string;
}

interface Scope {
  id: string;
  name: string;
  color: string;
}

interface TriageResponse {
  capture: QuickCapture;
  task: { id: string; title: string };
}

type FilterTab = 'all' | 'inbox' | 'triaged' | 'dismissed';

export function InboxPage() {
  const toast = useToast();
  useNow(); // tick every 30s so relative timestamps stay fresh
  const [captures, setCaptures] = useState<QuickCapture[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [captureScopes, setCaptureScopes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>('inbox');

  async function load() {
    try {
      const params = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const [caps, sc] = await Promise.all([
        apiGet<QuickCapture[]>(`/quick-captures${params}`),
        apiGet<Scope[]>('/scopes'),
      ]);
      setCaptures(caps);
      setScopes(sc);
    } catch (err) {
      console.error('Failed to load captures:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    load();
  }, [activeTab]);

  // Refresh when a quick capture is added from Header
  useEffect(() => {
    function onCapture() { load(); }
    window.addEventListener('quick-capture-added', onCapture);
    return () => window.removeEventListener('quick-capture-added', onCapture);
  }, [activeTab]);

  async function handleCreateTask(capture: QuickCapture) {
    try {
      const scopeId = captureScopes[capture.id];
      await apiPost<TriageResponse>(`/quick-captures/${capture.id}/triage-task`, {
        title: capture.text,
        scope_ids: scopeId ? [scopeId] : undefined,
      });
      toast.success('Task created from capture');
      load();
    } catch (err) {
      console.error('Failed to triage capture:', err);
      toast.error('Failed to create task from capture');
    }
  }

  async function handleCreateNote(capture: QuickCapture) {
    try {
      const scopeId = captureScopes[capture.id];
      await apiPost(`/quick-captures/${capture.id}/triage-note`, {
        title: capture.text,
        scope_ids: scopeId ? [scopeId] : undefined,
      });
      toast.success('Note created from capture');
      load();
    } catch (err) {
      console.error('Failed to triage capture to note:', err);
      toast.error('Failed to create note from capture');
    }
  }

  async function handleDismiss(id: string) {
    try {
      await apiPost(`/quick-captures/${id}/dismiss`, {});
      toast.success('Capture dismissed');
      load();
    } catch (err) {
      console.error('Failed to dismiss capture:', err);
      toast.error('Failed to dismiss capture');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this capture permanently?')) return;
    try {
      await apiDelete(`/quick-captures/${id}`);
      toast.success('Capture deleted');
      load();
    } catch (err) {
      console.error('Failed to delete capture:', err);
      toast.error('Failed to delete capture');
    }
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'inbox', label: 'Inbox' },
    { key: 'triaged', label: 'Triaged' },
    { key: 'dismissed', label: 'Dismissed' },
  ];

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Inbox</h1>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-bg-input rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-bg-surface text-text-primary font-medium shadow-sm'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Capture List */}
      {captures.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Inbox size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">
            {activeTab === 'inbox'
              ? 'Inbox is empty'
              : activeTab === 'triaged'
              ? 'No triaged captures'
              : activeTab === 'dismissed'
              ? 'No dismissed captures'
              : 'No captures yet'}
          </p>
          <p className="text-sm">
            Use the Quick Capture button in the header to jot down thoughts quickly.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {captures.map(capture => (
            <div
              key={capture.id}
              className={`px-4 py-3 bg-bg-card border border-border rounded-lg group transition-colors ${
                capture.status === 'dismissed' ? 'opacity-60' : ''
              }`}
            >
              <p
                className={`text-sm ${
                  capture.status === 'dismissed'
                    ? 'line-through text-text-muted'
                    : 'text-text-primary'
                }`}
              >
                {capture.text}
              </p>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-xs text-text-muted">
                  {formatRelativeTime(capture.created_at)}
                </span>
                <StatusBadge status={capture.status} />
                {capture.triaged_to_task_id && (
                  <Link
                    to={`/tasks/${capture.triaged_to_task_id}`}
                    className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors"
                  >
                    <ExternalLink size={10} />
                    View task
                  </Link>
                )}
                {capture.triaged_to_note_id && (
                  <Link
                    to={`/notes/${capture.triaged_to_note_id}`}
                    className="flex items-center gap-1 text-xs text-[#14b8a6] hover:text-[#2dd4bf] transition-colors"
                  >
                    <ExternalLink size={10} />
                    View note
                  </Link>
                )}
                <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {capture.status === 'inbox' && (
                    <>
                      {scopes.length > 0 && (
                        <select
                          value={captureScopes[capture.id] || ''}
                          onChange={(e) => setCaptureScopes(prev => ({ ...prev, [capture.id]: e.target.value }))}
                          className="px-1.5 py-1 bg-bg-input border border-border rounded text-xs text-text-primary max-w-[120px]"
                          title="Assign project"
                        >
                          <option value="">No project</option>
                          {scopes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                      <button
                        onClick={() => handleCreateTask(capture)}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                        title="Create task from this capture"
                      >
                        <CheckSquare size={12} />
                        Task
                      </button>
                      <button
                        onClick={() => handleCreateNote(capture)}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#14b8a6]/10 text-[#14b8a6] hover:bg-[#14b8a6]/20 transition-colors"
                        title="Create note from this capture"
                      >
                        <StickyNote size={12} />
                        Note
                      </button>
                      <button
                        onClick={() => handleDismiss(capture.id)}
                        className="p-1 text-text-muted hover:text-warning transition-colors"
                        title="Dismiss"
                      >
                        <X size={14} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(capture.id)}
                    className="p-1 text-text-muted hover:text-danger transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    inbox: 'bg-accent/10 text-accent',
    triaged: 'bg-success/10 text-success',
    dismissed: 'bg-text-muted/10 text-text-muted',
  };

  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[status] ?? ''}`}>
      {status}
    </span>
  );
}

